import { IAuthToken } from '@qima-labs/app/shared/models/interfaces/auth-token.interface';
import { ILoginInfo } from '@qima-labs/app/shared/models/interfaces/login-info.interface';
import { IUserInfo } from '@qima-labs/app/shared/models/interfaces/user-info.interface';
import { UserService } from '@qima-labs/app/shared/services/user-services/user.service';
import { LOGIN_URL, SERVER_URL } from '@qsp-ui-library/constants/app.constants';
import { EStorageKeys } from '@qsp-ui-library/models/enums/storage-keys.enum';
import { IHttpResponseBody } from '@qsp-ui-library/models/interfaces/http-response-body.interface';
import { IUser } from '@qsp-ui-library/models/interfaces/user.interface';
import { HttpService, SkipInterceptorService } from '@qsp-ui-library/services/http-services/http.service';
import { StorageService } from '@qsp-ui-library/services/storage-services/storage.service';
import { LogrocketInitService } from '@qsp-ui-library/services/third-party-services/logrocket-init/logrocket-init.service';
import { WINDOW } from '@qsp-ui-library/tokens/window.token';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CookieService } from 'ngx-cookie';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AppAuthenticationService {
  public readonly currentUserSubject$: Subject<IUser> = new Subject<IUser>();
  public readonly refreshTokenExpire$: Subject<number> = new Subject<number>();
  private readonly _office365LogoutURL = `https://login.microsoftonline.com/common/oauth2/logout?client_id=`;

  public constructor(
    private readonly _storageService: StorageService,
    private readonly _cookieService: CookieService,
    private readonly _httpService: HttpService,
    private readonly _httpSkipService: SkipInterceptorService,
    private readonly _router: Router,
    private readonly _userService: UserService,
    private readonly _logrocketInitService: LogrocketInitService,
    @Inject(WINDOW) private readonly _window: Window
  ) {}

  public updateUserData(data: Readonly<IUser>): void {
    this.currentUserSubject$.next(data);
  }

  public updateRefreshToken(data: number): void {
    this.refreshTokenExpire$.next(data);
  }

  public getAccessToken$(code: string): Observable<IHttpResponseBody<ILoginInfo>> {
    const url = `${SERVER_URL}/auth2-service-cloud/v1.0/security/azure/token?code=${code}`;

    return this._httpSkipService.httpClient.get<IHttpResponseBody<ILoginInfo>>(url, { observe: 'body' });
  }

  public handleLoginResponse(response: IHttpResponseBody<ILoginInfo>, redirectUrl?: string): void {
    if (response?.content?.authToken) {
      this.saveTokenAndExpiration(response.content.authToken);

      if (response?.content?.authClientId) {
        const authClientId = response?.content?.authClientId;

        this._cookieService.put(EStorageKeys.CLIENT_ID, authClientId);
      }

      if (response?.content?.userInfo) {
        this._logrocketInitService.identifyUserForLogRocket(response.content.userInfo);
        this._storageService.setItem(EStorageKeys.USER_DATA, JSON.stringify(response?.content?.userInfo));
        this._storageService.setItem(EStorageKeys.USER_ID, response?.content?.userInfo?.userId || '');

        this._cookieService.put(EStorageKeys.USER_DATA, JSON.stringify(response?.content?.userInfo));
        this._cookieService.put(EStorageKeys.USER_ID, response?.content?.userInfo?.userId || '');

        // only after get the permissions successfully, then redirect to the page
        this._userService.saveUserPermissionsToStore(redirectUrl);
      } else {
        /* the backend return unexpected data , redirect to /unauthorized page */
        void this._router.navigate(['/unauthorized']);
      }
    } else {
      console.error('can not find the token,the backend response is invalidate');
    }
  }

  public refreshAccessToken$(): Observable<IHttpResponseBody<IAuthToken>> {
    const token = this._cookieService.get(EStorageKeys.REFRESH_TOKEN) || '';
    const url = `${SERVER_URL}/auth2-service-cloud/v1.0/security/azure/token?refreshToken=${token}`;

    return this._httpService.httpClient.put(url, null, { observe: 'body' });
  }

  public refreshAccessToken(): void {
    void this.refreshAccessToken$()
      .pipe(untilDestroyed(this))
      .subscribe((response: IHttpResponseBody<IAuthToken>): void => {
        if (response?.content) {
          this.saveTokenAndExpiration(response.content);
          this._logrocketInitService.identifyUserForLogRocket({ id: this._cookieService.get(EStorageKeys.USER_ID) || '' } as unknown as IUserInfo);
        }
      });
  }

  public navigateToLogin(redirectUrl?: string): void {
    const redirect = this._buildLoginRedirectUrl(redirectUrl);
    const search = `?redirect=${encodeURIComponent(redirect)}`;

    if (this._window.location.href.toLowerCase().includes('localhost')) {
      this._window.location.assign(`${LOGIN_URL}${search}`);
    } else {
      this._window.location.assign(`${SERVER_URL}/auth2-service-cloud/v1.0/security/azure/login${search}`);
    }
  }

  public resolveRedirectUrl(redirectUrl: string): string {
    if (!/^https?:\/\//i.test(redirectUrl)) {
      return redirectUrl || '/';
    }

    try {
      const url = new URL(redirectUrl);

      if (url.origin !== this._window.location.origin) {
        return redirectUrl;
      }

      return `${url.pathname}${url.search}${url.hash}` || '/';
    } catch {
      return '/';
    }
  }

  public clearAuthenticationData(): void {
    this._storageService.clearAuthenticationData();
  }

  public logout(): void {
    this.deleteToken$().pipe(untilDestroyed(this)).subscribe();

    this.clearAuthenticationData();
    const clientId = this._cookieService.get(EStorageKeys.CLIENT_ID);

    this._window.location.replace(`${this._office365LogoutURL}${clientId}`);
  }

  public isAuthenticated(): boolean {
    return this._cookieService.get(EStorageKeys.USER_DATA) !== null;
  }

  public getCurrentUser(): IUser {
    return JSON.parse(this._cookieService.get(EStorageKeys.USER_DATA) || '{}');
  }

  public getCompanyId(): string {
    const userData = this.getCurrentUser();

    return userData.company?.id || '';
  }

  public deleteToken$(): Observable<void> {
    return this._httpService.httpClient
      .delete<void>(`${SERVER_URL}/auth2-service-cloud/v1.0/security/azure/token`, {
        observe: 'body',
      })
      .pipe(
        map((response: void): void => response),
        catchError((): Observable<void> => {
          return of<void>(undefined);
        })
      );
  }

  public saveTokenAndExpiration(token: IAuthToken): void {
    const { accessToken, accessTokenExpire, refreshToken, refreshTokenExpire } = token || {};

    if (accessToken) {
      // http-auth-interceptor use the cookie to get the token
      this._cookieService.put(EStorageKeys.AUTH_TOKEN, decodeURIComponent(accessToken as string).trim());
      this._cookieService.put(EStorageKeys.AUTH_TOKEN_EXPIRE, `${accessTokenExpire}`);

      this._storageService.setItem(EStorageKeys.AUTH_TOKEN, decodeURIComponent(accessToken as string).trim());
      this._storageService.setItem(EStorageKeys.AUTH_TOKEN_EXPIRE, `${accessTokenExpire}`);
    }

    if (refreshToken) {
      this._cookieService.put(EStorageKeys.REFRESH_TOKEN, refreshToken);
      this._cookieService.put(EStorageKeys.REFRESH_TOKEN_EXPIRE, `${refreshTokenExpire}`);

      this._storageService.setItem(EStorageKeys.REFRESH_TOKEN, refreshToken);
      this._storageService.setItem(EStorageKeys.REFRESH_TOKEN_EXPIRE, `${refreshTokenExpire}`);

      this.updateRefreshToken(refreshTokenExpire);
    }
  }

  private _buildLoginRedirectUrl(redirectUrl?: string): string {
    if (!redirectUrl) {
      return this._window.location.href;
    }

    if (/^https?:\/\//i.test(redirectUrl)) {
      return redirectUrl;
    }

    return `${this._window.location.origin}${redirectUrl.startsWith('/') ? redirectUrl : `/${redirectUrl}`}`;
  }
}
